import React from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Video,
  Building2,
  Zap,
  Shield,
  Cloud,
  Headphones,
  ArrowRight,
} from 'lucide-react';
import { authService } from '../services/auth.service.ts';
import { getUserPlan, type UserPlan } from '../utils/plan.ts';

type PlanId = 'free' | 'pro' | 'enterprise';

const plans: Array<{
  id: PlanId;
  name: string;
  description: string;
  price: string;
  period: string;
  features: string[];
  cta: string;
  popular: boolean;
  icon: typeof Video;
  gradient: string;
}> = [
  {
    id: 'free',
    name: 'Ücretsiz',
    description: 'Bireysel kullanım ve küçük ekipler için',
    price: '0₺',
    period: '/ay',
    features: [
      '50 dakikaya kadar toplantı',
      '10 katılımcı',
      'HD görüntü kalitesi',
      'Ekran paylaşımı',
      'Temel sohbet',
    ],
    cta: 'Ücretsiz Başla',
    popular: false,
    icon: Video,
    gradient: 'from-slate-600 to-slate-700',
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Büyüyen ekipler ve profesyoneller için',
    price: '279.90₺',
    period: '/ay',
    features: [
      'Sınırsız toplantı süresi',
      '100 katılımcı',
      '1440p görüntü kalitesi',
      'Bulut kayıt (10 GB)',
      'Bekleme odası',
      'Öncelikli destek',
    ],
    cta: "Pro'ya Geç",
    popular: true,
    icon: Zap,
    gradient: 'from-purple-600 to-pink-600',
  },
  {
    id: 'enterprise',
    name: 'Kurumsal',
    description: 'Şirketler ve kurumlar için özelleştirilmiş',
    price: 'Özel',
    period: '',
    features: [
      'Sınırsız her şey',
      '500+ katılımcı',
      'Özel sunucu seçeneği',
      'SSO & API entegrasyonu',
      'Dedicated hesap yöneticisi',
      'SLA garantisi',
    ],
    cta: 'İletişime Geç',
    popular: false,
    icon: Building2,
    gradient: 'from-purple-700 to-pink-700',
  },
];

const comparison = [
  { feature: 'Toplantı süresi', free: '50 dk', pro: 'Sınırsız', enterprise: 'Sınırsız' },
  { feature: 'Katılımcı sayısı', free: '10', pro: '100', enterprise: '500+' },
  { feature: 'Bulut kayıt', free: '—', pro: '10 GB', enterprise: 'Sınırsız' },
  { feature: 'Öncelikli destek', free: '—', pro: '✓', enterprise: '✓' },
  { feature: 'SSO / API', free: '—', pro: '—', enterprise: '✓' },
];

function getPlanAction(
  planId: PlanId,
  currentPlan: UserPlan,
  isAuthenticated: boolean
): { type: 'hidden' | 'current' | 'link'; href?: string; label?: string; primary?: boolean } {
  if (planId === currentPlan) {
    return { type: 'current', label: 'Mevcut planınız' };
  }

  if (planId === 'free') {
    if (!isAuthenticated) {
      return { type: 'link', href: '/register', label: 'Ücretsiz Başla', primary: false };
    }
    return { type: 'hidden' };
  }

  if (planId === 'pro') {
    if (!isAuthenticated) {
      return { type: 'link', href: '/login?redirect=/checkout?plan=pro', label: "Pro'ya Geç", primary: true };
    }
    return { type: 'link', href: '/checkout?plan=pro', label: "Pro'ya Geç", primary: true };
  }

  return { type: 'link', href: '/contact', label: 'İletişime Geç', primary: false };
}

function Pricing() {
  const isAuthenticated = authService.isAuthenticated();
  const currentPlan = getUserPlan(authService.getUser());

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-24 pb-20">
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Şeffaf <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Fiyatlandırma</span>
          </h1>
          <p className="text-xl text-gray-400">
            İhtiyacınıza uygun planı seçin. Kredi kartı gerekmeden ücretsiz deneyin.
          </p>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan) => {
              const Icon = plan.icon;
              const action = getPlanAction(plan.id, currentPlan, isAuthenticated);

              return (
                <div
                  key={plan.name}
                  className={`relative rounded-2xl border p-6 lg:p-8 flex flex-col transition transform hover:scale-[1.02] ${
                    plan.popular
                      ? 'bg-white/10 border-purple-500/50 shadow-xl shadow-purple-500/20'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-full">
                      En Popüler
                    </div>
                  )}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${plan.gradient} flex items-center justify-center mb-6`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">{plan.name}</h2>
                  <p className="text-gray-400 text-sm mb-6">{plan.description}</p>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-gray-400">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-gray-300">
                        <Check className="w-5 h-5 text-green-400 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {action.type === 'current' && (
                    <div className="w-full py-3 px-6 rounded-lg text-center text-sm font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                      {action.label}
                    </div>
                  )}

                  {action.type === 'link' && action.href && (
                    <Link
                      to={action.href}
                      className={`block w-full py-3 px-6 rounded-lg font-semibold text-center transition flex items-center justify-center gap-2 ${
                        action.primary
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg hover:shadow-purple-500/30'
                          : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                      }`}
                    >
                      {action.label}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">
            Planları Karşılaştır
          </h2>
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
            <div className="sm:hidden divide-y divide-white/10">
              {comparison.map((row, i) => (
                <div key={i} className="p-4">
                  <h4 className="text-white font-semibold mb-3">{row.feature}</h4>
                  <dl className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center">
                      <dt className="text-gray-500 text-xs mb-1">Ücretsiz</dt>
                      <dd className="text-gray-300">{row.free}</dd>
                    </div>
                    <div className="text-center">
                      <dt className="text-purple-400 text-xs mb-1 font-semibold">Pro</dt>
                      <dd className="text-gray-300">{row.pro}</dd>
                    </div>
                    <div className="text-center">
                      <dt className="text-gray-500 text-xs mb-1">Kurumsal</dt>
                      <dd className="text-gray-300">{row.enterprise}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>

            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-4 py-4 text-left text-gray-400 font-semibold">Özellik</th>
                    <th className="px-4 py-4 text-center text-white font-semibold">Ücretsiz</th>
                    <th className="px-4 py-4 text-center text-purple-400 font-semibold">Pro</th>
                    <th className="px-4 py-4 text-center text-white font-semibold">Kurumsal</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-4 text-gray-300">{row.feature}</td>
                      <td className="px-4 py-4 text-center text-gray-400">{row.free}</td>
                      <td className="px-4 py-4 text-center text-gray-300">{row.pro}</td>
                      <td className="px-4 py-4 text-center text-gray-300">{row.enterprise}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <div className="bg-white/5 rounded-xl border border-white/10 p-6">
              <Shield className="w-10 h-10 text-purple-400 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-1">KVKK Uyumlu</h3>
              <p className="text-gray-400 text-sm">Verileriniz Türkiye’de güvende</p>
            </div>
            <div className="bg-white/5 rounded-xl border border-white/10 p-6">
              <Cloud className="w-10 h-10 text-purple-400 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-1">İptal Kolay</h3>
              <p className="text-gray-400 text-sm">İstediğiniz zaman iptal edin</p>
            </div>
            <div className="bg-white/5 rounded-xl border border-white/10 p-6">
              <Headphones className="w-10 h-10 text-purple-400 mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-1">7/24 Destek</h3>
              <p className="text-gray-400 text-sm">Türkçe teknik destek</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Pricing;
